/**
 * The list surface. Server-side paging from the first screen, because a worklog or
 * work-item table will not survive client-side filtering once it is real.
 */
import type { ReactNode } from 'react'
import clsx from 'clsx'
import { Button, Spinner } from './ui'

export type Column<T> = {
  key: string
  header: string
  align?: 'left' | 'right' | 'center'
  width?: string
  render: (row: T) => ReactNode
}

export function DataTable<T extends { id: string }>({
  columns,
  rows,
  total,
  limit,
  offset,
  onPage,
  loading,
  onRowClick,
  empty,
}: {
  columns: Column<T>[]
  rows: T[]
  total: number
  limit: number
  offset: number
  onPage: (offset: number) => void
  loading?: boolean
  onRowClick?: (row: T) => void
  empty?: ReactNode
}) {
  const showing = rows.length
  return (
    <div className="card overflow-hidden fill-column">
      <div className="overflow-x-auto fill-scroll">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-[var(--color-rule-strong)] bg-[var(--color-surface-sunk)]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  style={{ width: column.width }}
                  className={clsx(
                    'px-3 py-2 text-[11px] font-medium tracking-wider text-[var(--color-ink-faint)] uppercase',
                    column.align === 'right' ? 'text-right' : 'text-left',
                    column.align === 'center' && 'text-center',
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-10 text-center">
                  <Spinner />
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={columns.length}>{empty}</td>
              </tr>
            )}
            {rows.map((row) => (
              <tr
                key={row.id}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={clsx(
                  'border-b border-[var(--color-rule)] last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-[var(--color-surface-sunk)]',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={clsx(
                      'px-3 py-2 text-[13px]',
                      column.align === 'right' && 'text-right tnum',
                      column.align === 'center' && 'text-center',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > limit && (
        <div className="flex items-center justify-between border-t border-[var(--color-rule)] px-3 py-2 text-[12px] text-[var(--color-ink-dim)]">
          <span className="tnum">
            {offset + 1}–{offset + showing} of {total}
          </span>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              disabled={offset === 0}
              onClick={() => onPage(Math.max(0, offset - limit))}
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={offset + showing >= total}
              onClick={() => onPage(offset + limit)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-lg font-semibold">{title}</h1>
        {subtitle && <p className="text-[13px] text-[var(--color-ink-dim)]">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </header>
  )
}
